import { issueTimeState } from '@/utils/issueWorkTime';
import { priorityToRiskLevel, riskLevelRank, riskLevelToPriority } from '@/utils/riskLevel';

export type IssueStatsStatus = '待接受' | '解决中' | '已解决' | '已延期';

export interface IssueStatsTaskLike {
  issueListStatus?: unknown;
  status?: unknown;
}

export interface IssueStatsPage<T> {
  records?: T[];
  total?: number;
  size?: number;
}

export async function fetchAllIssueChecklistPages<T>(
  fetchPage: (page: number, size: number) => Promise<IssueStatsPage<T>>,
  pageSize = 100,
): Promise<{ records: T[]; total: number }> {
  const first = await fetchPage(1, pageSize);
  const firstRecords = first.records ?? [];
  const total = Math.max(Number(first.total) || 0, firstRecords.length);
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const records = [...firstRecords];
  for (let page = 2; page <= pages; page += 1) {
    const next = await fetchPage(page, pageSize);
    records.push(...(next.records ?? []));
  }
  return { records, total };
}

export function mapIssueStatsStatus(task: IssueStatsTaskLike): IssueStatsStatus {
  const lifecycle = String(task.issueListStatus ?? '').trim();
  if (/^(已关闭|已解决|关闭|close|closed|resolved)$/i.test(lifecycle)) return '已解决';
  const status = String(task.status ?? '').trim();
  if (/^(延期|已延期)$/i.test(lifecycle) || status === '延期') return '已延期';
  if (
    /^(挂起|解决中|接收\/处理|处理中|in_progress|doing|进行中)$/i.test(lifecycle) ||
    status === '挂起' ||
    /^(in_progress|doing|进行中)$/i.test(status)
  ) {
    return '解决中';
  }
  if (/^(close|closed|done)$/i.test(status) || status === '关闭') return '已解决';
  return '待接受';
}

export function issueStatsSeverityFromPriority(priority: unknown) {
  return priorityToRiskLevel(priority);
}

export function issueStatsSeverityRank(priority: unknown) {
  return riskLevelRank(priority);
}

/** 规范为 P0–P3；兼容中文程度（中风险/高风险等），避免误落到 P3。 */
export function issueStatsNormalizePriority(priority: unknown): 'P0' | 'P1' | 'P2' | 'P3' {
  const raw = String(priority ?? '').trim().toUpperCase();
  if (raw === 'P0' || raw === 'P1' || raw === 'P2' || raw === 'P3') return raw;
  const mapped = riskLevelToPriority(priority);
  if (mapped === 'P0' || mapped === 'P1' || mapped === 'P2' || mapped === 'P3') return mapped;
  return 'P3';
}

export type IssueTimelyBucket = 'high' | 'mid' | 'low';

/** 柱状图分桶：高风险=P0/P1，中风险=P2，低风险=P3 */
export function issueTimelyBucketOf(priority: unknown): IssueTimelyBucket {
  const p = issueStatsNormalizePriority(priority);
  if (p === 'P0' || p === 'P1') return 'high';
  if (p === 'P2') return 'mid';
  return 'low';
}

export interface IssueTimelyRowLike {
  priority?: unknown;
  severity?: unknown;
  dueDate?: string | null;
  closedAt?: string | null;
  resolvedAt?: string | null;
  closed?: boolean;
}

/**
 * 问题及时完成率：该风险桶内「未逾期」占比。
 * 未设截止 / 进行中未逾期 / 按期关闭 计为及时；逾期进行中 / 逾期关闭 不计。
 */
export function issueTimelyRateFor(
  rows: IssueTimelyRowLike[],
  referenceDate = new Date(),
): number {
  if (!rows.length) return 0;
  let timely = 0;
  for (const row of rows) {
    const st = issueTimeState(
      {
        dueDate: row.dueDate || null,
        closed: !!row.closed,
        closedAt: row.closedAt || row.resolvedAt || null,
      },
      referenceDate,
    );
    if (st.code === 'closed-on-time' || st.code === 'active' || st.code === 'unset') {
      timely += 1;
    }
  }
  return Math.round((timely / rows.length) * 100);
}

export function issueTimelyBarsFromRows(
  rows: IssueTimelyRowLike[],
  referenceDate = new Date(),
): Array<{ key: IssueTimelyBucket; label: string; rate: number }> {
  const high = rows.filter((r) => issueTimelyBucketOf(r.priority ?? r.severity) === 'high');
  const mid = rows.filter((r) => issueTimelyBucketOf(r.priority ?? r.severity) === 'mid');
  const low = rows.filter((r) => issueTimelyBucketOf(r.priority ?? r.severity) === 'low');
  return [
    { key: 'high', label: '高风险', rate: issueTimelyRateFor(high, referenceDate) },
    { key: 'mid', label: '中风险', rate: issueTimelyRateFor(mid, referenceDate) },
    { key: 'low', label: '低风险', rate: issueTimelyRateFor(low, referenceDate) },
  ];
}

export interface IssueHandlerStatsInput {
  status: IssueStatsStatus;
  assigneeUserIds?: number[];
  handler?: string;
}

export interface IssueHandlerStatsBase {
  handler: string;
  total: number;
  resolved: number;
  unresolved: number;
  delayed: number;
}

/** 按处理人拆分统计；同一问题的多名处理人各计 1 条。 */
export function aggregateIssueHandlerStats(
  rows: IssueHandlerStatsInput[],
  resolveName: (userId: number) => string,
): IssueHandlerStatsBase[] {
  const map = new Map<string, IssueHandlerStatsBase>();
  for (const row of rows) {
    const ids = [...new Set((row.assigneeUserIds ?? []).map(Number).filter((id) => id > 0))];
    const names = ids.length ? ids.map(resolveName) : [row.handler || '—'];
    for (const name of names) {
      const current = map.get(name) ?? {
        handler: name,
        total: 0,
        resolved: 0,
        unresolved: 0,
        delayed: 0,
      };
      current.total += 1;
      if (row.status === '已解决') current.resolved += 1;
      else current.unresolved += 1;
      if (row.status === '已延期') current.delayed += 1;
      map.set(name, current);
    }
  }
  return [...map.values()].sort((a, b) => b.total - a.total || a.handler.localeCompare(b.handler, 'zh-CN'));
}
